// Pure PlayerRef helpers — how the v2 canvas identifies a player (no DOM/React).
//
// Identity is the THREE ids (player + competition + season): a player can appear
// in a league AND a cup in one season, so player+season alone is ambiguous.
// `displayName` is a label only — never part of identity (names collide; ids
// don't). This is the "bare minimum passed around" that Ben asked about, with the
// one correction that competition rides along too.

import type { PlayerRef, PlayerSearchResult, PlayerCurrentLeague } from "@catbento/shared";

/** Seed a ref from a search result. Competition + season come from the player's
 *  MOST-RECENT match (the search payload carries them), which is exactly the
 *  "default to the current season" behaviour — resolved to concrete ids up front
 *  so the ref is never ambiguous. They can be null for a player with no matches;
 *  the widget then leaves comp/season unset and the user picks. */
export function refFromSearch(r: PlayerSearchResult): PlayerRef {
  return {
    playerId: r.id,
    competitionId: r.mostRecentCompetitionId,
    seasonId: r.mostRecentSeasonId,
    displayName: r.name,
  };
}

/**
 * Upgrade a search-seeded ref to the player's resolved CURRENT domestic league
 * (C2b) — the search "most recent" competition can be a cup or a national-team
 * game, so this replaces comp+season with the resolver's. Returns the SAME ref
 * (by identity) when there's nothing to change — the resolver found no domestic
 * football (null), or the ref already matches — so callers can skip re-setting.
 */
export function applyCurrentLeague(ref: PlayerRef, league: PlayerCurrentLeague): PlayerRef {
  if (league.competitionId === null || league.seasonId === null) return ref;
  if (ref.competitionId === league.competitionId && ref.seasonId === league.seasonId) return ref;
  return { ...ref, competitionId: league.competitionId, seasonId: league.seasonId };
}

/** A stable dedup key over the identity ids only (not the label). Two seasons of
 *  the same player are DIFFERENT keys — so comparing 24/25 vs 25/26 is allowed,
 *  matching the prototype. Null comp/season are part of the key (an unresolved
 *  ref is distinct from a resolved one). */
export function refKey(ref: PlayerRef): string {
  return `${ref.playerId}:${ref.competitionId ?? ""}:${ref.seasonId ?? ""}`;
}

/** Same player entity (identity ids), ignoring the display label. */
export function sameRef(a: PlayerRef, b: PlayerRef): boolean {
  return refKey(a) === refKey(b);
}
